import { cn } from '@/lib/utils';

/** Identifies one of the three Portfolio page tabs. */
export type PortfolioTab = 'positions' | 'open-orders' | 'history';

export interface PortfolioTabsProps {
  /** Currently active tab. */
  activeTab: PortfolioTab;
  /** Fired when a tab is clicked. */
  onTabChange: (tab: PortfolioTab) => void;
  /**
   * Optional counts shown in parentheses next to the labels for the
   * positions and open-orders tabs (history has no count).
   */
  counts?: { positions?: number; openOrders?: number };
  className?: string;
}

interface TabDescriptor {
  id: PortfolioTab;
  label: string;
  count?: number;
}

/**
 * Bottom-border underline tab strip used by the Portfolio page.
 *
 * Three equally-weighted tabs: Positions, Open Orders, History. The
 * active tab is rendered in semibold purple with a 2px purple bottom
 * border; inactive tabs are light gray with a faint divider — matching
 * Figma node 1761:89382 styles `style_QD3ITV` and `style_I37JFG`.
 *
 * @example
 * ```tsx
 * <PortfolioTabs
 *   activeTab={tab}
 *   onTabChange={setTab}
 *   counts={{ positions: 40, openOrders: 100 }}
 * />
 * ```
 */
export function PortfolioTabs({ activeTab, onTabChange, counts, className }: PortfolioTabsProps) {
  const tabs: TabDescriptor[] = [
    { id: 'positions', label: 'Positions', count: counts?.positions },
    { id: 'open-orders', label: 'Open Orders', count: counts?.openOrders },
    { id: 'history', label: 'History' },
  ];

  return (
    <div role="tablist" aria-label="Portfolio sections" className={cn('flex w-full', className)}>
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={isActive}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              'flex-1 flex items-center justify-center gap-1 py-2 text-base font-sans cursor-pointer transition-colors whitespace-nowrap text-sm',
              'border-b-2',
              isActive
                ? 'font-semibold text-purple border-purple'
                : 'font-normal text-ink-muted border-ink-muted/50 hover:text-primary',
            )}
          >
            <span>{tab.label}</span>
            {typeof tab.count === 'number' && <span>({tab.count})</span>}
          </button>
        );
      })}
    </div>
  );
}
